import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getRepairRequest,
  postRepairRequest,
  deleteRepairRequest as deleteRepairRequestAPI,
  RepairRequestParams,
} from './api';
import {
  Status,
  GetRepairRequest,
  PostRepairRequest,
  PostRepairRequestQc,
} from '../../services/apiTypes';
import { SESSION_STORAGE_KEY } from '../../_constants/Const';
import { isGetRepairRequest } from '../../_utils/typeGuardUtils';

interface RepairRequestState {
  status: Status;
  repairRequest?: GetRepairRequest;
}

const initialState: RepairRequestState = {
  status: 'idle',
};

export const fetchRepairRequest = createAsyncThunk(
  'repairRequest/fetchRepairRequest',
  async (params: RepairRequestParams, { rejectWithValue }) => {
    try {
      return await getRepairRequest(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createRepairRequest = createAsyncThunk(
  'repairRequest/createRepairRequest',
  async (
    newRequest: PostRepairRequest | PostRepairRequestQc,
    { rejectWithValue },
  ) => {
    try {
      return await postRepairRequest(newRequest);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteRepairRequest = createAsyncThunk(
  'repairRequest/deleteRepairRequest',
  async (rmaNo: string, { rejectWithValue }) => {
    try {
      return await deleteRepairRequestAPI(rmaNo);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const repairRequestSlice = createSlice({
  name: 'repairRequest',
  initialState,
  reducers: {
    setRepairRequest: (state, action: PayloadAction<GetRepairRequest>) => {
      state.repairRequest = action.payload;
    },
    setStatus: (state, action: PayloadAction<number>) => {
      if (state.repairRequest) {
        state.repairRequest.status = action.payload;
      }
    },
    resetRepairRequest: (state) => {
      state.repairRequest = undefined;
      sessionStorage.removeItem(SESSION_STORAGE_KEY.RMA_NO);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRepairRequest.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRepairRequest.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetRepairRequest(action.payload)) {
          state.repairRequest = action.payload;
          sessionStorage.setItem(
            SESSION_STORAGE_KEY.RMA_NO,
            action.payload.rmaNo,
          );
        }
      })
      .addCase(fetchRepairRequest.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(createRepairRequest.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createRepairRequest.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createRepairRequest.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(deleteRepairRequest.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteRepairRequest.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteRepairRequest.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { setRepairRequest, resetRepairRequest, setStatus } =
  repairRequestSlice.actions;

export default repairRequestSlice.reducer;
